import { AnalyticsBrowser } from '@segment/analytics-next';
import { createContext, useCallback, useContext, useMemo } from 'react';
import { isMobile } from 'react-device-detect';

const searchParams = new URLSearchParams(window.location.search);
const _console = searchParams.get('console');

const AnalyticsContext = createContext<AnalyticsBrowser>(undefined!);

type Props = {
  writeKey: string;
  children: React.ReactNode;
};

export const AnalyticsProvider = ({ children, writeKey }: Props) => {
  const analytics = useMemo(() => AnalyticsBrowser.load({ writeKey }), [writeKey]);
  return <AnalyticsContext.Provider value={analytics}>{children}</AnalyticsContext.Provider>;
};

export const useAnalytics = () => {
  const segmentAnalytics = useContext(AnalyticsContext);

  if (!segmentAnalytics) {
    throw new Error('Context used outside of its Provider!');
  }

  if (process.env.REACT_APP_DISABLE_ANALYTICS === '1' || _console === 'true') {
    (segmentAnalytics as any).track = (eventName: string, params: {}) => {
      console.log('[Analytics Segment Mock]', eventName, params);
    };
  }

  const track = useCallback(
    (eventName: string, params: Record<string, any> = {}) => {
      const trackDefaultProperties = {
        appType: 'website',
        isMobile,
      };
      segmentAnalytics.track(eventName, {
        ...trackDefaultProperties,
        ...params,
      });
    },
    [segmentAnalytics]
  );

  /**
   * It sets ajs_anonymous_id cookie if needed
   */
  const setAnalyticsCookieIfNeeded = useCallback(async () => {
    return (await segmentAnalytics.user()).anonymousId();
  }, [segmentAnalytics]);

  const result = useMemo(() => {
    return {
      ...segmentAnalytics,
      setAnalyticsCookieIfNeeded,
      track,
    };
  }, [segmentAnalytics, track, setAnalyticsCookieIfNeeded]);

  return result;
};
